// Co-agency letter → document HTML.
//
// Ported from the admin portal's LetterPreview and shared by BOTH letter screens, so
// the letter an agent previews while drafting is the letter HQ issues: same letterhead
// from document_settings, same `letter_body_template`, same signature block.
//
// This matters more than it looks. The create screen used to preview a hardcoded body
// kept in the page file, which drifted from the real template the moment HQ edited it —
// the agent proof-read one letter and sent another.

import { isAmountBasis, letterCoaShareLabel, letterCidShareLabel } from './coAgencyShare';
import { letterheadHtml } from './documentLetterhead';

export const escapeHtml = (s) => String(s ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

/**
 * Substitute {{token}}s; unknown tokens are left in place so gaps stay visible.
 *
 * `flatShare` handles the one place a token owns the character after it. The saved
 * body template reads "...on a {{coa_sharing_ratio}}%:{{cid_sharing_ratio}}% basis...",
 * which is right while a letter can only quote ratios. Once it can also quote a flat
 * sum, substituting "RM 5,000.00" leaves a stray "%" glued to the money — so on a
 * flat-amount letter these two tokens consume the "%" that follows them and put back
 * the whole phrase ("RM 5,000.00", "balance").
 *
 * The alternative was to make HQ rewrite the template in document_settings before any
 * flat-amount letter could print correctly, on pain of the document stating something
 * nobody agreed. {{coa_share}} / {{cid_share}} are what a rewritten template should
 * use; this keeps the one already saved right in the meantime. Mirrors the admin
 * portal's replaceVariables().
 */
export const fillTemplate = (tpl, vars, flatShare = false) => {
  let out = pickDealWords(String(tpl || ''), vars.letter_type);
  if (flatShare) {
    // The pair first: "30%:70%" is fine tight, "RM 5,000.00:balance" is not, so the
    // whole X%:Y% construct is replaced as one unit and gets spaces round the colon.
    out = out.replace(
      /\{\{\s*coa_sharing_ratio\s*\}\}\s*%\s*:\s*\{\{\s*cid_sharing_ratio\s*\}\}\s*%/g,
      (m) => (vars.coa_share != null && vars.cid_share != null ? `${vars.coa_share} : ${vars.cid_share}` : m),
    );
    // Then either token on its own, for a template that doesn't pair them.
    out = out
      .replace(/\{\{\s*coa_sharing_ratio\s*\}\}\s*%/g, (m) => (vars.coa_share != null ? String(vars.coa_share) : m))
      .replace(/\{\{\s*cid_sharing_ratio\s*\}\}\s*%/g, (m) => (vars.cid_share != null ? String(vars.cid_share) : m));
  }
  return out.replace(/\{\{\s*(\w+)\s*\}\}/g, (m, k) => (vars[k] != null ? vars[k] : m));
};

/**
 * The saved template words clause 2a) for both deal types: "selling / rental
 * transaction", "rental/sale amount". A letter is one or the other, so keep only the
 * word that applies. Runs on the template before any value is filled in, so an
 * address that happens to read "Rent/Sale" is never touched. Mirrors the admin
 * portal's pickDealWords().
 */
const DEAL_WORD_PAIR = /\b(selling|sales?|rental|rent)\s*\/\s*(selling|sales?|rental|rent)\b/gi;
const isRentWord = (w) => /^rent/i.test(w);

export const pickDealWords = (html, letterType) => {
  if (letterType !== 'RENT' && letterType !== 'SALES') return html;
  const wantRent = letterType === 'RENT';
  return html.replace(DEAL_WORD_PAIR, (m, a, b) => {
    if (isRentWord(a) === isRentWord(b)) return m;
    return isRentWord(a) === wantRent ? a : b;
  });
};

/**
 * Number → words, Malaysian ringgit style.
 *
 * The " And " before a sub-hundred remainder is NOT decorative — it is how the admin
 * portal and the booking-form server both spell amounts ("One Million Two Hundred And
 * Fifty Thousand"). The letter template substitutes {{price_words}}, so dropping it
 * made the same letter read differently depending on which app rendered it.
 */
export function numberToWords(numValue) {
  const ones = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine', 'Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
  const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];
  const scales = ['', 'Thousand', 'Million', 'Billion'];
  const convert = (n) => {
    if (n < 20) return ones[n];
    if (n < 100) return `${tens[Math.floor(n / 10)]}${n % 10 ? ` ${ones[n % 10]}` : ''}`;
    return `${ones[Math.floor(n / 100)]} Hundred${n % 100 ? ` And ${convert(n % 100)}` : ''}`;
  };
  const intPart = Math.floor(Math.abs(Number(numValue) || 0));
  if (intPart === 0) return 'Zero';
  let result = '';
  let scaleIndex = 0;
  let remaining = intPart;
  while (remaining > 0) {
    const chunk = remaining % 1000;
    if (chunk > 0) result = `${convert(chunk)}${scales[scaleIndex] ? ` ${scales[scaleIndex]}` : ''}${result ? ` ${result}` : ''}`;
    remaining = Math.floor(remaining / 1000);
    scaleIndex += 1;
  }
  return result.trim();
}

const money = (n) => `RM ${(Number(n) || 0).toLocaleString('en-MY', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/**
 * Letterhead + body + signature block.
 *
 * `letter` is either a saved row or the draft the create screen is holding — the only
 * fields read are the ones both shapes carry. Anything that is not the issued state
 * stamps a watermark, so a preview or a voided letter can never be passed off as the
 * live one. (VERIFIED is the enum value for "issued"; the stamp says DRAFT for PENDING.)
 */
export function buildLetterHtml(letter, settings, agent) {
  const s = settings || {};
  const l = letter || {};
  const priceNum = parseFloat(l.price_rm) || 0;

  const vars = {
    letter_ref: escapeHtml(l.letter_ref || ''),
    letter_date: l.letter_date
      ? new Date(l.letter_date).toLocaleDateString('en-MY', { day: '2-digit', month: 'long', year: 'numeric' })
      : '',
    letter_type: escapeHtml(l.letter_type || ''),
    price_label: l.letter_type === 'RENT' ? 'Rental' : 'Selling',
    property_address: escapeHtml(l.property_address || '-'),
    price_rm: money(priceNum),
    price_words: numberToWords(priceNum),
    cid_agent_name: escapeHtml(agent?.full_name || '-'),
    cid_agent_ren_no: escapeHtml(agent?.ren_no || '-'),
    co_agency_name: escapeHtml(l.co_agency_name || l.co_agency || '-'),
    co_agency_address: escapeHtml(l.co_agency_address || '-'),
    co_agency_phone: escapeHtml(l.co_agency_phone || '-'),
    co_agency_registration_no: escapeHtml(l.co_agency_registration_no || '-'),
    co_agency_agent_name: escapeHtml(l.co_agency_agent_name || '-'),
    co_agency_agent_contact: escapeHtml(l.co_agency_agent_contact || '-'),
    co_agency_agent_ren_no: escapeHtml(l.co_agency_agent_ren_no || '-'),
    coa_sharing_ratio: String(Math.round(parseFloat(l.ca_share ?? l.coa_sharing_ratio ?? 50))),
    cid_sharing_ratio: String(Math.round(parseFloat(l.cid_share ?? l.cid_sharing_ratio ?? 50))),
    // Named shares, for the two shapes a letter can quote. On a flat-amount letter
    // the ratio tokens above are 0 and mean nothing — fillTemplate substitutes
    // these instead, swallowing the literal "%" the template writes after them.
    coa_share: letterCoaShareLabel(l),
    cid_share: letterCidShareLabel(l),
    tax_percentage: String(Math.round(parseFloat(l.tax_percentage ?? 8))),
  };

  const bodyHtml = fillTemplate(s.letter_body_template || '', vars, isAmountBasis(l.share_basis ?? l.sharing_basis));
  const statusRaw = String(l.status_raw || l.status || '').toUpperCase();
  const watermarkColour = statusRaw === 'VOIDED' ? 'rgba(0,0,0,0.08)'
    : statusRaw === 'REJECTED' ? 'rgba(255,77,79,0.12)'
      : 'rgba(0,0,0,0.10)';
  // PENDING is the unissued state, so the stamp has to read DRAFT — printing the enum
  // verbatim would put "PENDING" across a document nobody is waiting on.
  const watermarkLabel = statusRaw === 'PENDING' ? 'DRAFT' : statusRaw;
  const watermark = statusRaw && statusRaw !== 'VERIFIED'
    ? `<div class="letter-watermark"><div style="color:${watermarkColour}">${escapeHtml(watermarkLabel)}</div></div>`
    : '';

  return `
    ${watermark}
    <div style="margin-bottom:12px">${letterheadHtml(s)}</div>
    <hr style="border:none;border-top:1px solid #000;margin:8px 0" />
    <div style="margin-bottom:12px">${bodyHtml}</div>
    <div class="letter-signatures" style="display:flex;justify-content:space-between;margin-top:20px">
      <div style="width:45%">
        <div>Yours in service,</div>
        <div style="font-weight:bold;margin-top:8px">${escapeHtml(s.company_name || '')}</div>
        <div style="height:90px">
          ${s.signatory_signature_url ? `<img src="${s.signatory_signature_url}" alt="Signature" style="height:50px;margin-top:8px" />` : ''}
          ${s.company_chop_url ? `<img src="${s.company_chop_url}" alt="Chop" style="height:58px;margin-left:6px;margin-top:8px" />` : ''}
        </div>
        <div style="border-top:1px dotted #000;padding-top:4px">
          <div style="font-weight:bold">${escapeHtml(s.signatory_name || '')} (${escapeHtml(s.signatory_ren_no || '')})</div>
          <div style="font-weight:bold">${escapeHtml(s.signatory_title || '')}</div>
        </div>
      </div>
      <div style="width:45%">
        <div>We hereby confirm and accept the above</div>
        <div style="margin-top:8px">&nbsp;</div>
        <div style="height:90px"></div>
        <div style="border-top:1px dotted #000;padding-top:4px">
          <div>Principal Name :</div>
          <div>Date :</div>
          <div>Company Chop :</div>
        </div>
      </div>
    </div>`;
}

/**
 * Prints the letter 7/6 larger, as every CID document prints (owner, 2026-10-08:
 * bigger text, same proportions — see styles.hbs on the servers), shrunk just
 * enough to stay on ONE A4 page when that size would not.
 *
 * The body is HQ's template plus whatever the agent typed (addresses, names), so no
 * fixed spacing can promise one page. This lays the sheet out at the page's width and
 * tries zoom 7/6, then 116%, 115% … on `.letter-content` until the sheet fits the
 * printable height; the first that fits becomes the print-only rule. One PAGE_H /
 * height step is not enough: zoom also widens the lines, so fewer of them wrap, and a
 * single step shrinks further than it needs to. Below MIN the text gets too small to
 * sign against, so a letter that still will not fit there prints at the full 7/6 and
 * runs onto page 2 (the signature block stays whole).
 *
 * The zoom is on the content, never the sheet, so the margins (the sheet's padding)
 * stay 15mm whatever the zoom. Measured with the sheet forced to the page
 * geometry, not as it sits on screen: the phone viewer shows it at 800px and the
 * admin print window at whatever width the popup opened, and either would
 * mis-measure. Vertical padding is dropped and `flow-root` keeps the content's own
 * margins inside the sheet, as the print padding does, so the height read is the
 * content box that has to fit 267mm.
 */
const FIT_SCRIPT = `(function(){
  var PAGE_H = 267 * 96 / 25.4, FULL = 7 / 6, MIN = 80;
  function fit() {
    var sheet = document.querySelector('.letter-sheet'), content = document.querySelector('.letter-content');
    if (!sheet || !content) return;
    var sheetCss = sheet.style.cssText, contentCss = content.style.cssText, best = 0;
    sheet.style.cssText = sheetCss + ';width:210mm;max-width:none;padding:0 15mm;box-sizing:border-box;display:flow-root';
    var zooms = [FULL];
    for (var z = Math.floor(FULL * 100); z >= MIN; z--) zooms.push(z / 100);
    for (var i = 0; i < zooms.length; i++) {
      content.style.zoom = zooms[i];
      if (sheet.getBoundingClientRect().height <= PAGE_H) { best = zooms[i]; break; }
    }
    sheet.style.cssText = sheetCss;
    content.style.cssText = contentCss;
    var tag = document.getElementById('letter-fit');
    if (!tag) { tag = document.createElement('style'); tag.id = 'letter-fit'; document.head.appendChild(tag); }
    tag.textContent = '@media print{.letter-content{zoom:' + (best || FULL) + '}}';
  }
  fit();
  window.addEventListener('load', fit);
  window.addEventListener('beforeprint', fit);
})();`;

/**
 * Wrap the letter body in the printable A4 shell the in-app viewer renders.
 *
 * The margins — 15mm on every side, as on every CID document — are drawn INSIDE the
 * sheet, as padding, on screen and in print alike, and the page itself has none
 * (`@page{margin:0}`). Page margins would leave room for Chrome's default "Headers
 * and footers" (date, title, about:blank, 1/1), which then print across the letter;
 * with none there is nowhere to draw them. In print the sheet is the full 210mm page,
 * and `box-decoration-break:clone` repeats the padding on page 2 when a letter runs
 * that long, so that page has a top margin too. A browser that cannot clone a
 * block's padding (Safari) gets a real 15mm page margin instead — the same margin on
 * every page — exactly as the server-rendered documents do (styles.hbs).
 *
 * DocViewer's Original mode lays the document out inside an 800px div
 * (`.doc-viewer-page`); in print that width is released here, or the 800px sheet
 * would be squeezed onto the 794px page and shrink every glyph. The watermark is
 * pinned to the page in print so DRAFT / VOIDED stamps every page, whatever the
 * preview positioned it with. The phone's Readable mode drops the sheet padding — it
 * has its own.
 */
export function letterDocument(innerHtml, title) {
  return `<html><head><title>${escapeHtml(title || 'Co-Agency Letter')}</title><style>@page{size:A4;margin:15mm}@supports (box-decoration-break:clone){@page{margin:0}}html,body{margin:0;padding:0}body{font-family:'Times New Roman',Times,serif;font-size:13px;line-height:1.3;color:#000;background:#fff}.letter-sheet{box-sizing:border-box;width:100%;max-width:210mm;padding:15mm;position:relative}@media print{body{overflow:hidden}.doc-viewer-page{width:auto!important}.letter-sheet{max-width:none;padding:0}@supports (box-decoration-break:clone){.letter-sheet{padding:15mm;box-decoration-break:clone}}.letter-watermark{position:fixed!important;top:0!important;left:0!important;right:0!important;bottom:0!important}}.mobile-doc-view .letter-sheet{max-width:none;padding:0}p{margin:0;min-height:1.3em}img{max-width:100%}.letter-signatures{break-inside:avoid;page-break-inside:avoid}.letter-watermark{position:fixed;top:0;left:0;right:0;bottom:0;display:flex;align-items:center;justify-content:center;pointer-events:none;z-index:10}.letter-watermark>div{transform:rotate(-35deg);font-size:72px;font-weight:bold;letter-spacing:8px;white-space:nowrap;text-transform:uppercase}</style></head><body><div class="letter-sheet"><div class="letter-content">${innerHtml}</div></div><script>${FIT_SCRIPT}</script></body></html>`;
}
